"use client";

import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import CommonHeader from "./common/CommonHeader";
import { OneTimePackagesGrid } from "./packages/OneTimePackagesGrid";
import { SubscriptionPlansGrid } from "./packages/SubscriptionPlansGrid";
import { AddOnsTable } from "./packages/AddOnsTable";
import {
  PackageFormDialog,
  PackageFormData,
} from "./packages/PackageFormDialog";
import { AddOnFormDialog, AddOnFormData } from "./packages/AddOnFormDialog";
import {
  SubscriptionPlanFormDialog,
  SubscriptionPlanFormData,
} from "./packages/SubscriptionPlanFormDialog";
import { AddOn, OneTimePackage } from "@/types";
import { subscriptionService } from "@/services/subscriptionService";
import { featureService } from "@/services/featureService";
import { Subscription } from "@/types/subscription.types";
import { packageService } from "@/services/packageService";

export function PackageManagement() {
  const {
    oneTimePackages,
    subscriptionPlans,
    // addOns,
    addOneTimePackage,
    updateOneTimePackage,
    deleteOneTimePackage,
    addSubscriptionPlan,
    updateSubscriptionPlan,
  } = useApp();

  const [isAddPackageDialogOpen, setIsAddPackageDialogOpen] = useState(false);
  const [isEditPackageDialogOpen, setIsEditPackageDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<OneTimePackage | null>(null);
  const [isAddSubscriptionPlanDialogOpen, setIsAddSubscriptionPlanDialogOpen] =
    useState(false);
  const [isEditSubscriptionPlanDialogOpen, setIsEditSubscriptionPlanDialogOpen] =
    useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<Subscription | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<OneTimePackage | null>(null);
  const [isPackageDeleteDialogOpen, setIsPackageDeleteDialogOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);


  const [packageForm, setPackageForm] = useState<PackageFormData>({
    name: "",
    price: 0,
    description: "",
    services: "",
  });

  const [subscriptionPlanForm, setSubscriptionPlanForm] =
    useState<SubscriptionPlanFormData>({
      name: "",
      price: 0,
      features: [],
    });

  const [addOnForm, setAddOnForm] = useState<AddOnFormData>({
    name: "",
    description: "",
    price: 0,
    category: "interior",
    active: true,
  });

  const resetPackageForm = () => {
    setPackageForm({
      name: "",
      price: 0,
      description: "",
      services: "",
    });
  };

  const resetSubscriptionPlanForm = () => {
    setSubscriptionPlanForm({
      name: "",
      price: 0,
      features: [],
    });
  };

  // Helper function to enrich subscription features with names
  const enrichSubscriptionsWithFeatureNames = async (
    subscriptions: Subscription[]
  ): Promise<Subscription[]> => {
    try {
      const allFeatures = await featureService.getAllFeatures();
      const featuresMap = new Map(allFeatures.map((f) => [f.id, f.name]));

      // Create deep copies to ensure React detects changes
      return subscriptions.map((sub) => ({
        ...sub,
        features: sub.features.map((feature) => ({
          id: feature.id,
          name: feature.name || featuresMap.get(feature.id) || "Unknown Feature",
          type: feature.type,
          value: feature.value,
        })),
      }));
    } catch (error) {
      console.error("Failed to fetch features for enrichment:", error);
      return subscriptions;
    }
  };

  // Centralized function to refresh subscriptions
  const refreshSubscriptions = async (showLoading = false) => {
    try {
      if (showLoading) {
        setIsLoadingSubscriptions(true);
      }
      const data = await subscriptionService.getAllSubscriptions();
      const filteredData = data.filter((sub) => !sub.isDeleted);
      const enrichedData = await enrichSubscriptionsWithFeatureNames(filteredData);
      
      // Create completely new array with deep copies to ensure React detects changes
      const newSubscriptions = enrichedData.map((sub) => ({ 
        id: sub.id,
        name: sub.name,
        price: sub.price,
        duration: sub.duration,
        isDeleted: sub.isDeleted,
        createdAt: sub.createdAt,
        updatedAt: sub.updatedAt,
        features: sub.features.map((f) => ({
          id: f.id,
          name: f.name,
          type: f.type,
          value: f.value,
        }))
      }));
      
      // Use functional update to ensure state change is detected
      setSubscriptions(() => [...newSubscriptions]);
    } catch (error) {
      console.error("Failed to refresh subscriptions:", error);
      toast.error("Failed to refresh subscription plans");
    } finally {
      if (showLoading) {
        setIsLoadingSubscriptions(false);
      }
    }
  };

  useEffect(() => {
    refreshSubscriptions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleAddPackage = async () => {
    if (!packageForm.name || packageForm.price <= 0 || !packageForm.services) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Transform services (newline-separated string) to features array
      const featuresArray = packageForm.services
        .split("\n")
        .filter((s) => s.trim());

      // Refresh packages by calling context method
      await addOneTimePackage({
        name: packageForm.name,
        price: packageForm.price,
        description: packageForm.description,
        services: featuresArray,
        active: true,
      });

      // Close dialog and reset form only on success
      setIsAddPackageDialogOpen(false);
      resetPackageForm();
      toast.success("Package added successfully");
    } catch (error) {
      console.error("Failed to add package:", error);
      toast.error("Failed to add package. Please try again.");
    }
  };

  const handleEditPackage = async () => {
    if (!editingPackage) return;

    if (!packageForm.name || packageForm.price <= 0 || !packageForm.services) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      // Transform services (newline-separated string) to features array
      const featuresArray = packageForm.services
        .split("\n")
        .filter((s) => s.trim());

      // Call API to update package
      await packageService.updatePackage(editingPackage.id, {
        name: packageForm.name,
        price: packageForm.price,
        features: featuresArray,
      });

      // Update local state via context
      updateOneTimePackage(editingPackage.id, {
        name: packageForm.name,
        price: packageForm.price,
        description: packageForm.description,
        services: featuresArray,
      });

      toast.success("Package updated successfully");
      setIsEditPackageDialogOpen(false);
      setEditingPackage(null);
      resetPackageForm();
    } catch (error) {
      console.error("Failed to update package:", error);
      toast.error("Failed to update package. Please try again.");
    }
  };

  const handleEditPackageClick = (pkg: OneTimePackage) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name,
      price: pkg.price,
      description: pkg.description,
      services: pkg.services.join("\n"), // Convert array to newline-separated string
    });
    setIsEditPackageDialogOpen(true);
  };

  const handleAddSubscriptionPlan = async () => {
    if (!subscriptionPlanForm.name || subscriptionPlanForm.price <= 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate that all features have id, type, and value
    const invalidFeatures = subscriptionPlanForm.features.filter(
      (f) => !f.id || !f.type || !f.value
    );
    if (invalidFeatures.length > 0) {
      toast.error("Please complete all feature fields");
      return;
    }

    try {
      // Transform form data to API payload format
      const payload = {
        name: subscriptionPlanForm.name.toUpperCase(),
        price: subscriptionPlanForm.price,
        features: subscriptionPlanForm.features.map((f) => ({
          id: f.id,
          type: f.type,
          value: f.value,
        })),
      };

      await subscriptionService.addSubscription(payload);
      
      // Close dialog and reset form first
      setIsAddSubscriptionPlanDialogOpen(false);
      resetSubscriptionPlanForm();
      
      // Small delay to ensure API has processed the new subscription
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Refresh subscriptions to update the grid
      await refreshSubscriptions();

      toast.success("Subscription plan added successfully");
    } catch (error) {
      console.error("Failed to add subscription plan:", error);
      toast.error("Failed to add subscription plan");
    }
  };

  const handleEditSubscriptionPlan = async () => {
    if (!editingSubscription) return;

    if (!subscriptionPlanForm.name || subscriptionPlanForm.price <= 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate that all features have id, type, and value
    const invalidFeatures = subscriptionPlanForm.features.filter(
      (f) => !f.id || !f.type || !f.value
    );
    if (invalidFeatures.length > 0) {
      toast.error("Please complete all feature fields");
      return;
    }

    try {
      // Transform form data to API payload format
      const payload = {
        name: subscriptionPlanForm.name.toUpperCase(),
        price: subscriptionPlanForm.price,
        features: subscriptionPlanForm.features.map((f) => ({
          id: f.id,
          type: f.type,
          value: f.value,
        })),
      };

      await subscriptionService.updateSubscription(editingSubscription.id, payload);
      
      // Refresh subscriptions to update the grid
      await refreshSubscriptions();

      toast.success("Subscription plan updated successfully");
      setIsEditSubscriptionPlanDialogOpen(false);
      setEditingSubscription(null);
      resetSubscriptionPlanForm();
    } catch (error) {
      console.error("Failed to update subscription plan:", error);
      toast.error("Failed to update subscription plan");
    }
  };

  const handleDeleteClick = (subscription: Subscription) => {
    setSubscriptionToDelete(subscription);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteSubscriptionPlan = async () => {
    if (!subscriptionToDelete) return;

    const subscriptionId = subscriptionToDelete.id;

    try {
      // Perform the actual deletion
      await subscriptionService.deleteSubscription(subscriptionId);
      
      // Close dialog and clear state first
      setIsDeleteDialogOpen(false);
      setSubscriptionToDelete(null);

      // Refresh subscriptions to update the grid
      await refreshSubscriptions();

      toast.success("Subscription plan deleted successfully");
    } catch (error) {
      console.error("Failed to delete subscription plan:", error);
      // Refresh from server to get the actual state in case of error
      await refreshSubscriptions();
      toast.error("Failed to delete subscription plan");
    }
  };

  const handleEditClick = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setSubscriptionPlanForm({
      name: subscription.name,
      price: subscription.price,
      features: subscription.features.map((f) => ({
        id: f.id,
        // Normalize type to lowercase to match form expectations
        type: (f.type.toLowerCase() === "number" ? "number" : "boolean") as "boolean" | "number",
        // Ensure value is always a string
        value: String(f.value),
      })),
    });
    setIsEditSubscriptionPlanDialogOpen(true);
  };

  const handleDeactivatePackage = (packageId: string, active: boolean) => {
    // If deactivating (active is false, meaning we're setting it to false), show delete confirmation
    if (!active) {
      const pkg = oneTimePackages.find((p) => p.id === packageId);
      if (pkg) {
        setPackageToDelete(pkg);
        setIsPackageDeleteDialogOpen(true);
      }
    } else {
      // If activating, just update the active status (though this shouldn't happen with current UI)
      updateOneTimePackage(packageId, { active: true });
    }
  };

  const handleDeletePackage = async () => {
    if (!packageToDelete) return;

    const packageId = packageToDelete.id;

    try {
      // Perform the actual deletion
      await deleteOneTimePackage(packageId);
      
      // Close dialog and clear state
      setIsPackageDeleteDialogOpen(false);
      setPackageToDelete(null);
    } catch (error) {
      console.error("Failed to delete package:", error);
      toast.error("Failed to delete package");
    }
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonHeader
        title="Package Management"
        description="Manage service packages and add-ons"
      />

      <Tabs defaultValue="subscription">
        <TabsList>
          <TabsTrigger value="subscription">Subscription Plans</TabsTrigger>
          <TabsTrigger value="one-time">One-Time Packages</TabsTrigger>
        </TabsList>

        {/* One-Time Packages */}
        <TabsContent value="one-time" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setIsAddPackageDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Package
            </Button>
          </div>
          <OneTimePackagesGrid
            packages={oneTimePackages}
            onToggleActive={handleDeactivatePackage}
            onEdit={handleEditPackageClick}
          />
        </TabsContent>

        {/* Subscription Plans */}
        <TabsContent value="subscription" className="space-y-4">
          <div className="flex justify-end">
            {/* <Button onClick={() => setIsAddSubscriptionPlanDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Subscription Plan
            </Button> */}
          </div>
          <SubscriptionPlansGrid 
            plans={subscriptionPlans} 
            subscriptions={subscriptions}
            isLoading={isLoadingSubscriptions}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        </TabsContent>
      </Tabs>

      {/* Add Package Dialog */}
      <PackageFormDialog
        open={isAddPackageDialogOpen}
        onOpenChange={setIsAddPackageDialogOpen}
        formData={packageForm}
        onFormDataChange={setPackageForm}
        onSave={handleAddPackage}
        onCancel={() => {
          setIsAddPackageDialogOpen(false);
          resetPackageForm();
        }}
      />

      {/* Edit Package Dialog */}
      <PackageFormDialog
        open={isEditPackageDialogOpen}
        onOpenChange={setIsEditPackageDialogOpen}
        formData={packageForm}
        onFormDataChange={setPackageForm}
        onSave={handleEditPackage}
        onCancel={() => {
          setIsEditPackageDialogOpen(false);
          setEditingPackage(null);
          resetPackageForm();
        }}
        isEditMode={true}
      />

      {/* Add Subscription Plan Dialog */}
      <SubscriptionPlanFormDialog
        open={isAddSubscriptionPlanDialogOpen}
        onOpenChange={setIsAddSubscriptionPlanDialogOpen}
        formData={subscriptionPlanForm}
        onFormDataChange={setSubscriptionPlanForm}
        onSave={handleAddSubscriptionPlan}
        onCancel={() => {
          setIsAddSubscriptionPlanDialogOpen(false);
          resetSubscriptionPlanForm();
        }}
      />

      {/* Edit Subscription Plan Dialog */}
      <SubscriptionPlanFormDialog
        open={isEditSubscriptionPlanDialogOpen}
        onOpenChange={setIsEditSubscriptionPlanDialogOpen}
        formData={subscriptionPlanForm}
        onFormDataChange={setSubscriptionPlanForm}
        onSave={handleEditSubscriptionPlan}
        onCancel={() => {
          setIsEditSubscriptionPlanDialogOpen(false);
          setEditingSubscription(null);
          resetSubscriptionPlanForm();
        }}
        isEditMode={true}
      />

      {/* Delete Subscription Plan Alert Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              subscription plan &quot;{subscriptionToDelete?.name}&quot; and remove it from
              the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setSubscriptionToDelete(null);
              }}
              className="cursor-pointer"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSubscriptionPlan}
              className="bg-destructive text-destructive-foreground text-white cursor-pointer hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Package Alert Dialog */}
      <AlertDialog open={isPackageDeleteDialogOpen} onOpenChange={setIsPackageDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              package &quot;{packageToDelete?.name}&quot; and remove it from
              the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setIsPackageDeleteDialogOpen(false);
                setPackageToDelete(null);
              }}
              className="cursor-pointer"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePackage}
              className="bg-destructive text-destructive-foreground text-white cursor-pointer hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
